/**
 * Exit Popup Component
 *
 * Desktop: exit intent (mouse leave from top).
 * Mobile: opens after a few seconds (no scroll required).
 * Manages cookie storage to prevent multiple displays.
 */

const MOBILE_DELAY_MS = 5000; // 5 seconds on mobile/tablet

class ExitPopup {
  constructor(options = {}) {
    this.options = {
      cookieName: 'pipefy_exit_popup',
      cookieExpiration: 365, // days
      mouseLeaveThreshold: 10, // pixels from top (desktop)
      mobileDelayMs: MOBILE_DELAY_MS,
      ...options,
    };

    this.hasExited = false;
    this.scrollPosition = 0;
    this.startTime = Date.now();
    this.isClosed = false;
    this.mobileTimerId = null;
    this.openedByTriggerLink = false;
    this.selectorBoundElements = new WeakSet();

    this.init();
  }

  /**
   * Initialize the detector.
   */
  init() {
    // Check if popup exists in DOM
    const popup = document.querySelector('.exit-popup');
    if (!popup) {
      return; // No popup to show
    }

    // Triggers from flexible rules: exit intent, link URL, CSS selector(s)
    this.exitIntentTriggerEnabled = popup.dataset.triggerExitIntent === '1';

    // Setup click tracking for banner links
    this.setupClickTracking(popup);

    // Open on link click — always attach when configured (ignores cookie)
    this.initLinkTrigger(popup);

    // Open on CSS selector click (e.g. .btn-book-a-demo, #botao-sucesso)
    this.initSelectorTrigger(popup);

    // Exit intent / mobile delay: skip if cookie exists (user already saw the popup)
    if (this.hasCookie()) {
      return;
    }

    // Exit intent: desktop mouse leave + mobile 5s (when rule is enabled)
    this.initDesktopDetection();
    this.initMobileDetection();
  }

  /**
   * Normalize URL for comparison (same path = same link).
   *
   * @param {string} url URL string (can be relative)
   * @return {string} Normalized full URL
   */
  normalizeUrl(url) {
    if (!url || typeof url !== 'string') {
      return '';
    }
    try {
      return new URL(url, window.location.origin).href;
    } catch {
      return url;
    }
  }

  /**
   * Get the first N links on the page that match the trigger URL (top to bottom in DOM order).
   *
   * @param {string} triggerLinkUrl Configured URL to match
   * @param {number} count Max number of links to return
   * @param {HTMLElement} popup Popup element (links inside it are excluded)
   * @return {HTMLAnchorElement[]}
   */
  getMatchingTriggerLinks(triggerLinkUrl, count, popup) {
    const normalizedTrigger = this.normalizeUrl(triggerLinkUrl);
    if (!normalizedTrigger) {
      return [];
    }
    const allLinks = document.body.querySelectorAll('a[href]');
    const matching = [];
    for (const link of allLinks) {
      if (popup && popup.contains(link)) {
        continue;
      }
      if (this.normalizeUrl(link.getAttribute('href')) === normalizedTrigger) {
        matching.push(link);
        if (matching.length >= count) {
          break;
        }
      }
    }
    return matching;
  }

  /**
   * Initialize link trigger: when user clicks a link matching the configured URL, open popup.
   * Only the first N matching links (top to bottom) trigger the popup.
   */
  initLinkTrigger(popup) {
    const triggerLinkUrl = popup.dataset.triggerLink;
    if (!triggerLinkUrl) {
      return;
    }
    const count = Math.max(1, parseInt(popup.dataset.triggerLinkCount || '1', 10));

    const handleClick = e => {
      const link = e.target.closest('a');
      if (!link || !link.href) {
        return;
      }
      const matching = this.getMatchingTriggerLinks(triggerLinkUrl, count, popup);
      if (!matching.length) {
        return;
      }
      const isTriggerLink = matching.includes(link);
      if (!isTriggerLink) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.triggerExitIntent({ fromTriggerLink: true });
    };

    document.addEventListener('click', handleClick, true);
  }

  /**
   * Get CSS trigger selectors from popup data attribute (data-trigger-selectors).
   *
   * @param {HTMLElement} popup Popup element
   * @return {string[]}
   */
  getTriggerSelectors(popup) {
    const raw =
      popup.getAttribute('data-trigger-selectors') || popup.dataset.triggerSelectors || '';
    return this.parseTriggerSelectors(raw);
  }

  /**
   * Parse CSS selectors from data attribute (JSON array). Handles HTML-encoded values.
   *
   * @param {string} raw
   * @return {string[]}
   */
  parseTriggerSelectors(raw) {
    if (!raw || typeof raw !== 'string') {
      return [];
    }
    let str = raw.trim();
    if (!str) {
      return [];
    }
    try {
      const parsed = JSON.parse(str);
      if (Array.isArray(parsed)) {
        return parsed.filter(s => typeof s === 'string' && s.trim());
      }
      return [];
    } catch {
      // Try decoding HTML entities in case attribute was stored encoded
      const decoded = str
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, '&');
      try {
        const parsed = JSON.parse(decoded);
        return Array.isArray(parsed) ? parsed.filter(s => typeof s === 'string' && s.trim()) : [];
      } catch {
        // Single selector as plain string (no JSON array)
        if (str.indexOf('[') !== 0 && str.trim()) {
          return [str.trim()];
        }
      }
    }
    return [];
  }

  /**
   * Open popup from trigger (shared handler for delegation and direct bind).
   *
   * @param {Event} e Click event
   * @param {HTMLElement} popup Popup element
   */
  handleSelectorClick(e, popup) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    this.triggerExitIntent({ fromTriggerLink: true });
  }

  /**
   * Bind click to every element in the page that matches one of the selectors (entire document, including footer).
   * Re-runs after short delays to catch late-rendered content. Uses selectorBoundElements to avoid duplicate listeners.
   *
   * @param {HTMLElement} popup Popup element
   * @param {string[]} selectors CSS selectors from dashboard
   */
  bindAllMatchingElements(popup, selectors) {
    const bindOne = el => {
      if (!el || this.selectorBoundElements.has(el) || popup.contains(el)) {
        return;
      }
      this.selectorBoundElements.add(el);
      el.addEventListener('click', e => this.handleSelectorClick(e, popup), true);
    };
    selectors.forEach(selector => {
      try {
        document.querySelectorAll(selector).forEach(bindOne);
      } catch {
        // Invalid selector, skip
      }
    });
  }

  /**
   * Initialize CSS selector trigger: scan entire page for class/id from dashboard and open popup on click.
   * Uses both document-level delegation and direct bind on all matching elements so header, body and footer are covered.
   */
  initSelectorTrigger(popup) {
    const selectors = this.getTriggerSelectors(popup);
    if (!selectors.length) {
      return;
    }

    const handleClick = e => {
      const target = e.target;
      if (!target || typeof target.closest !== 'function') {
        return;
      }
      if (popup.contains(target)) {
        return;
      }
      for (const selector of selectors) {
        try {
          const matched = target.closest(selector);
          if (matched && !popup.contains(matched)) {
            this.handleSelectorClick(e, popup);
            return;
          }
        } catch {
          // Invalid selector, skip
        }
      }
    };

    document.addEventListener('click', handleClick, true);

    // Bind every matching element on the page so class/id from dashboard always opens modal (including footer)
    this.bindAllMatchingElements(popup, selectors);
    setTimeout(() => this.bindAllMatchingElements(popup, selectors), 300);
    setTimeout(() => this.bindAllMatchingElements(popup, selectors), 1200);
  }

  /**
   * Setup click tracking for banner links.
   *
   * @param {HTMLElement} popup Popup element
   */
  setupClickTracking(popup) {
    const bannerLink = popup.querySelector('.exit-popup-banner');
    if (bannerLink) {
      bannerLink.addEventListener('click', () => {
        this.trackEvent('exit_popup_clicked', {
          popup_id: popup.dataset.popupId || null,
          popup_type: popup.dataset.popupType || null,
        });
      });
    }
  }

  /**
   * Initialize desktop exit intent detection (mouse leave from top).
   * Only on desktop devices — no timer; mobile gets the 5s delay in initMobileDetection instead.
   */
  initDesktopDetection() {
    if (!this.exitIntentTriggerEnabled) {
      return;
    }
    // Only on desktop (by user-agent). Mobile devices use 5s delay only, no mouse leave.
    if (this.isMobileDevice()) {
      return;
    }

    document.addEventListener('mouseleave', e => {
      if (this.hasExited || this.hasCookie()) {
        return;
      }

      // Check if mouse left from the top of the page
      if (e.clientY <= this.options.mouseLeaveThreshold) {
        this.triggerExitIntent();
      }
    });
  }

  /**
   * Initialize mobile: show popup after 5 seconds. Only for mobile/tablet (user-agent), not desktop.
   */
  initMobileDetection() {
    if (!this.exitIntentTriggerEnabled) {
      return;
    }
    if (!this.isMobileDevice()) {
      return;
    }

    const delay = this.options.mobileDelayMs ?? MOBILE_DELAY_MS;
    this.mobileTimerId = window.setTimeout(() => {
      this.mobileTimerId = null;
      if (this.hasExited || this.hasCookie()) {
        return;
      }
      this.triggerExitIntent();
    }, delay);
  }

  /**
   * Trigger exit intent - show popup.
   *
   * @param {object} [options]
   * @param {boolean} [options.fromTriggerLink] When true, always open (ignore cookie) and do not set cookie on close.
   */
  triggerExitIntent(options = {}) {
    const fromTriggerLink = Boolean(options.fromTriggerLink);
    if (!fromTriggerLink && (this.hasExited || this.hasCookie())) {
      return;
    }

    if (this.mobileTimerId !== null) {
      window.clearTimeout(this.mobileTimerId);
      this.mobileTimerId = null;
    }

    this.hasExited = true;
    this.openedByTriggerLink = Boolean(options.fromTriggerLink);

    // Dispatch custom event
    const event = new CustomEvent('exitIntentDetected', {
      detail: {
        timestamp: Date.now(),
        scrollPosition: this.scrollPosition,
        timeOnPage: Date.now() - this.startTime,
      },
    });

    document.dispatchEvent(event);

    // Show popup if exists in DOM
    this.showPopup();
  }

  /**
   * Show popup if it exists in DOM.
   */
  showPopup() {
    const popup = document.querySelector('.exit-popup');
    if (!popup) {
      return;
    }

    // Allow close button to work again (e.g. when reopening via trigger link)
    this.isClosed = false;

    // Add visible class
    popup.classList.remove('is-hidden');
    popup.classList.add('is-visible');

    // Focus management for accessibility
    const closeButton = popup.querySelector('.exit-popup-close');
    if (closeButton) {
      closeButton.focus();
    }

    // Track analytics event
    this.trackEvent('exit_popup_viewed', {
      popup_id: popup.dataset.popupId || null,
      popup_type: popup.dataset.popupType || null,
    });

    // Setup close handlers
    this.setupCloseHandlers(popup);
  }

  /**
   * Setup close handlers for popup.
   *
   * @param {HTMLElement} popup Popup element
   */
  setupCloseHandlers(popup) {
    const closeButton = popup.querySelector('.exit-popup-close');
    const overlay = popup.querySelector('.exit-popup-overlay');

    // Close button
    if (closeButton) {
      closeButton.addEventListener('click', () => this.closePopup(popup));
    }

    // Overlay click
    if (overlay) {
      overlay.addEventListener('click', () => this.closePopup(popup));
    }

    // ESC key
    const escHandler = e => {
      if (e.key === 'Escape' && popup.classList.contains('is-visible')) {
        this.closePopup(popup);
        document.removeEventListener('keydown', escHandler);
      }
    };
    document.addEventListener('keydown', escHandler);
  }

  /**
   * Close popup.
   *
   * @param {HTMLElement} popup Popup element
   */
  closePopup(popup) {
    if (!popup || this.isClosed) {
      return;
    }

    // Mark as closed to prevent multiple calls
    this.isClosed = true;

    // Track analytics event
    this.trackEvent('exit_popup_closed', {
      popup_id: popup.dataset.popupId || null,
      popup_type: popup.dataset.popupType || null,
    });

    // Set cookie to prevent future displays — skip when opened by trigger link so user can open again
    if (!this.openedByTriggerLink) {
      this.setCookie();
    } else {
      this.hasExited = false;
    }

    // Animate out
    popup.classList.add('is-closing');
    popup.classList.remove('is-visible');

    setTimeout(() => {
      popup.classList.remove('is-closing');
      popup.classList.add('is-hidden');
    }, 300);
  }

  /**
   * Track analytics event.
   *
   * @param {string} eventName Event name
   * @param {object} eventData Event data
   */
  trackEvent(eventName, eventData = {}) {
    // Use existing tracking system if available
    if (window.PipefyTracking && typeof window.PipefyTracking.track === 'function') {
      window.PipefyTracking.track(eventName, eventData);
    }

    // Google Analytics 4
    if (window.gtag && typeof window.gtag === 'function') {
      window.gtag('event', eventName, eventData);
    }

    // Google Analytics Universal
    if (window.ga && typeof window.ga === 'function') {
      window.ga('send', 'event', 'Exit Popup', eventName, eventData.popup_id || 'unknown');
    }

    // Custom event for other trackers
    const customEvent = new CustomEvent('pipefyExitPopupEvent', {
      detail: {
        event: eventName,
        data: eventData,
      },
    });
    document.dispatchEvent(customEvent);
  }

  /**
   * Check if device is mobile/tablet by user-agent only.
   * Used to apply 5s delay only on mobile; desktop uses mouse leave only (no timer).
   *
   * @return {boolean}
   */
  isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }

  /**
   * Check if cookie exists.
   *
   * @return {boolean}
   */
  hasCookie() {
    return this.getCookie(this.options.cookieName) !== null;
  }

  /**
   * Set cookie to prevent future displays.
   * Only called when user explicitly closes the modal.
   */
  setCookie() {
    // Double check: only set cookie if popup was actually closed by user
    if (!this.isClosed) {
      return;
    }

    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + this.options.cookieExpiration);

    const cookieValue = '1';
    const cookieString = `${this.options.cookieName}=${cookieValue}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;

    document.cookie = cookieString;
  }

  /**
   * Get cookie value.
   *
   * @param {string} name Cookie name
   * @return {string|null} Cookie value or null
   */
  getCookie(name) {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1, cookie.length);
      }
      if (cookie.indexOf(nameEQ) === 0) {
        return cookie.substring(nameEQ.length, cookie.length);
      }
    }

    return null;
  }

  /**
   * Clear cookie (for testing purposes).
   */
  clearCookie() {
    document.cookie = `${this.options.cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }
}

/**
 * Initialize ExitPopup when DOM is ready or if already ready.
 * Retries once after load and once with a short delay so we catch the popup even if it appears late (e.g. after wp_footer).
 */
function initializeExitPopup() {
  const popup = document.querySelector('.exit-popup');
  if (popup && !popup.dataset.initialized) {
    popup.dataset.initialized = 'true';
    new ExitPopup();
    return true;
  }
  return false;
}

function scheduleExitPopupInit() {
  if (initializeExitPopup()) {
    return;
  }
  if (document.readyState === 'complete') {
    setTimeout(initializeExitPopup, 50);
    return;
  }
  window.addEventListener('load', () => initializeExitPopup(), { once: true });
  setTimeout(initializeExitPopup, 150);
}

// Auto-initialize: try at DOMContentLoaded, then retry on load and after 100ms so popup is never missed
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleExitPopupInit);
} else {
  scheduleExitPopupInit();
}

// Export for use in modules
export default ExitPopup;

// Make available globally
if (typeof window !== 'undefined') {
  window.ExitPopup = ExitPopup;
}
